'use client';

import { memo, useState } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import CreditRatingIcons from '@/assets/icons/CreditRatingIcons';
import NextButton from '@/components/Button/NextButton';
import { CreditRating, FormData } from '@/types/form';
import { creditRatingFactCard, fieldRules } from '@/utils/form';
import OptionButtons from '../OptionButtons/OptionButtons';
import { WarningIcon } from '@/assets/icons/WarningIcon';
import generalStyles from '../FormBody.module.scss';
import useModal from '@/hooks/useModal';
import styles from './StepEight.module.scss';

interface Props {
  formData?: FormData;
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  isModal?: boolean;
  verificationError?: boolean;
}

const CreditRatingForm = ({
  formData,
  register,
  handleSubmit,
  control,
  setValue,
  trigger,
  isModal = false,
  verificationError = false,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const [selected, setSelected] = useState(formData?.creditRating);
  const handleSelect = (value: CreditRating) => {
    setSelected(value);
    setValue?.('creditRating', value);
    trigger?.('creditRating');
  };

  const submitForm = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit?.();
    if (!errors?.loan_reason) {
      closeModal();
    }
  };

  const selectStep = () => {
    if (!isModal) {
      handleSubmit?.();
    }
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      <div className={styles.optionsWrapper}>
        {register && (
          <input
            type="text"
            className={generalStyles.hiddenInput}
            id="creditRating"
            {...register('creditRating', fieldRules.creditRating)}
          />
        )}
        <OptionButtons
          icon={<CreditRatingIcons icon="excellent" />}
          text={CreditRating.excellent}
          selected={selected === CreditRating.excellent}
          second_text={`$${creditRatingFactCard(CreditRating.excellent).amount}M funded`}
          onClick={() => {
            handleSelect(CreditRating.excellent);
            selectStep();
          }}
        />
        <OptionButtons
          icon={<CreditRatingIcons icon="good" />}
          text={CreditRating.good}
          selected={selected === CreditRating.good}
          second_text={`$${creditRatingFactCard(CreditRating.good).amount}M funded`}
          onClick={() => {
            handleSelect(CreditRating.good);
            selectStep();
          }}
        />
        <OptionButtons
          icon={<CreditRatingIcons icon="fair" />}
          text={CreditRating.fair}
          selected={selected === CreditRating.fair}
          second_text={`$${creditRatingFactCard(CreditRating.fair).amount}M funded`}
          onClick={() => {
            handleSelect(CreditRating.fair);
            selectStep();
          }}
        />
        <OptionButtons
          icon={<CreditRatingIcons icon="poor" />}
          text={CreditRating.poor}
          selected={selected === CreditRating.poor}
          second_text={`$${creditRatingFactCard(CreditRating.poor).amount}M funded`}
          onClick={() => {
            handleSelect(CreditRating.poor);
            selectStep();
          }}
        />
        {errors?.creditRating && (
          <span className={generalStyles.error}>
            {errors.creditRating.message}
          </span>
        )}
      </div>
      {isModal && (
        <NextButton
          buttonType="submit"
          type="button"
          text="Apply changes"
          styles={`${generalStyles.button} ${isModal ? generalStyles.smallButton : ''}`}
          isArrow={!isModal}
        />
      )}
      {verificationError && (
        <p
          className={`${generalStyles.error} ${generalStyles.verificationError}`}
        >
          <WarningIcon className={generalStyles.warningIcon} />
          <span>We couldn&apos;t verify that you are human</span>
        </p>
      )}
    </form>
  );
};

export default memo(CreditRatingForm);
